import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ShieldCheck, Leaf, Sparkles } from "lucide-react";
import { Reveal, Overline, EASE } from "@/components/Reveal";

const PLANS = {
  residential: [
    {
      name: "Signature Regular",
      price: "From $140 NZD",
      desc: "Weekly or fortnightly upkeep that keeps your home effortlessly pristine.",
      items: ["All rooms dusted & surfaces polished", "Kitchens & bathrooms sanitised", "Floors vacuumed & mopped", "Mirrors & glass detailed", "Linen change on request"],
      testid: "plan-res-regular",
    },
    {
      name: "Deep Clean Revival",
      price: "From $320 NZD",
      desc: "A meticulous top-to-bottom reset for seasons, guests or simply because.",
      items: ["Everything in Signature Regular", "Inside ovens, rangehoods & cabinetry", "Skirting, sills & light fittings", "Grout & tile detailing", "Interior windows"],
      testid: "plan-res-deep",
    },
    {
      name: "Move In / Move Out",
      price: "From $380 NZD",
      desc: "Bond-ready cleans that satisfy the sharpest property manager.",
      items: ["Full interior deep clean", "Oven & appliance detailing", "Cupboards inside & out", "Wall spot-cleaning", "Bond-back checklist provided"],
      testid: "plan-res-move",
    },
  ],
  commercial: [
    {
      name: "Executive Office Care",
      price: "Custom pricing",
      desc: "Discreet after-hours cleaning for offices, studios and consulting suites.",
      items: ["Desks, phones & touchpoints sanitised", "Kitchens & bathrooms serviced", "Floors & carpet care", "Bins & consumables managed", "Flexible nightly / weekly schedules"],
      testid: "plan-com-office",
    },
    {
      name: "Retail & Showroom",
      price: "Custom pricing",
      desc: "Front-of-house perfection so your product — and your space — shines.",
      items: ["Glass, mirrors & displays polished", "High-traffic floor care", "Fitting rooms & counters detailed", "Pre-open or post-close scheduling", "Event & launch support"],
      testid: "plan-com-retail",
    },
    {
      name: "Commercial Deep Reset",
      price: "Custom pricing",
      desc: "Quarterly or post-renovation deep cleans for larger commercial spaces.",
      items: ["Full-site sanitisation", "High-level dusting & vents", "Carpet & hard-floor restoration", "Washroom deep detailing", "Compliance documentation"],
      testid: "plan-com-deep",
    },
  ],
};

const ADDONS = [
  "Oven & rangehood detailing",
  "Interior window & track cleaning",
  "Fridge & pantry refresh",
  "Marble & stone polishing",
  "Laundry & ironing service",
  "Carpet steam refresh",
];

export default function Services() {
  const [tab, setTab] = useState("residential");

  return (
    <motion.main
      data-testid="services-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: EASE }}
      className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28"
    >
      <Reveal>
        <Overline>Our Services</Overline>
        <h1 className="mt-4 max-w-2xl font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl lg:text-6xl">
          Every space, polished to a <em className="text-gold-gradient not-italic">golden</em> finish
        </h1>
        <p className="mt-6 max-w-xl text-sm leading-relaxed text-stone sm:text-base">
          Choose residential or commercial care — every visit follows the same
          five-star housekeeping protocol, delivered by vetted TidyCare specialists.
        </p>
      </Reveal>

      <Reveal delay={0.15} className="mt-12">
        <div data-testid="service-tabs" className="inline-flex rounded-full border border-sand bg-white p-1.5">
          {[
            { key: "residential", label: "Residential", testid: "tab-residential" },
            { key: "commercial", label: "Commercial", testid: "tab-commercial" },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => setTab(t.key)}
              className={`rounded-full px-7 py-3 text-xs font-bold uppercase tracking-[0.18em] transition-colors duration-300 ${
                tab === t.key ? "btn-gold" : "text-stone hover:text-gold-dark"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </Reveal>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="mt-12 grid gap-6 lg:grid-cols-3"
        >
          {PLANS[tab].map((p) => (
            <article
              key={p.name}
              data-testid={p.testid}
              className="flex flex-col rounded-3xl border border-sand bg-white p-8 transition-shadow duration-500 hover:shadow-[0_24px_50px_-24px_rgba(154,123,56,0.35)]"
            >
              <h2 className="font-serif text-2xl font-medium text-ink">{p.name}</h2>
              <p className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-gold-dark" data-testid={`${p.testid}-price`}>
                {p.price}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-stone">{p.desc}</p>
              <ul className="mt-6 flex-1 space-y-3 border-t border-sand pt-6">
                {p.items.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-ink/80">
                    <Check size={15} className="mt-0.5 shrink-0 text-gold" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid={`${p.testid}-quote-btn`}
                className="btn-gold mt-8 rounded-full px-6 py-3.5 text-center text-xs font-bold uppercase tracking-[0.18em]"
              >
                Request This Service
              </Link>
            </article>
          ))}
        </motion.div>
      </AnimatePresence>

      {tab === "residential" && (
        <p data-testid="residential-pricing-note" className="mt-8 max-w-2xl text-sm leading-relaxed text-stone">
          <span className="font-bold uppercase tracking-[0.16em] text-gold-dark">A note on pricing — </span>
          every home is different, so residential pricing depends on the size of
          your home and the type of clean you choose. The prices above are a
          starting guide only; your free quote is always tailored to your space.
        </p>
      )}

      <section data-testid="addons-section" className="mt-24 lg:mt-32">
        <Reveal>
          <Overline>Bespoke Add-Ons</Overline>
          <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl">
            The finishing touches
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ADDONS.map((a, i) => (
            <Reveal key={a} delay={i * 0.06}>
              <div
                data-testid={`addon-${i}`}
                className="flex items-center gap-4 rounded-2xl border border-sand bg-white px-6 py-5 transition-colors duration-300 hover:border-gold/50"
              >
                <Sparkles size={16} className="shrink-0 text-gold" />
                <span className="text-sm font-medium text-ink">{a}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="guarantee-section" className="mt-24 rounded-3xl bg-obsidian px-8 py-16 text-center lg:mt-32 lg:py-20">
        <Reveal>
          <div className="mx-auto flex max-w-3xl flex-col items-center">
            <div className="flex gap-6 text-gold">
              <ShieldCheck size={28} />
              <Leaf size={28} />
              <Sparkles size={28} />
            </div>
            <h2 className="mt-6 font-serif text-3xl font-medium text-ivory sm:text-4xl">
              The TidyCare Guarantee
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-ivory/70 sm:text-base">
              Every clean is backed by our 24-hour re-clean promise: if any detail
              falls short of the golden standard, we return and perfect it — free.
              Fully insured, security-vetted teams, eco-conscious products only.
            </p>
            <Link
              to="/contact"
              data-testid="guarantee-quote-btn"
              className="btn-gold mt-9 rounded-full px-9 py-4 text-xs font-bold uppercase tracking-[0.2em]"
            >
              Get a Free Quote
            </Link>
          </div>
        </Reveal>
      </section>
    </motion.main>
  );
}
